import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ThumbsUp, Heart, Laugh, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const META = {
  like: { icon: ThumbsUp, color: "#8012C6" },
  love: { icon: Heart, color: "#E024A3" },
  laugh: { icon: Laugh, color: "#FFB800" },
  wow: { icon: Sparkles, color: "#00A3B4" },
};

export default function ReactionInsights({ postId, open, onClose }) {
  const navigate = useNavigate();
  const { data = [], isLoading } = useQuery({
    queryKey: ["post-reactions", postId],
    queryFn: async () => (await api.get(`/posts/${postId}/reactions`)).data,
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="reaction-insights-modal" className="bg-card border-border rounded-2xl max-w-sm">
        <DialogTitle className="font-heading font-bold">Reactions ({data.length})</DialogTitle>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#8012C6] animate-spin" /></div>
        ) : data.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No reactions yet.</p>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto space-y-1">
            {data.map(({ user: u, type }) => {
              const M = META[type] || META.like;
              const Icon = M.icon;
              return (
                <button
                  key={u.user_id}
                  data-testid={`reactor-${u.user_id}`}
                  onClick={() => { navigate(`/profile/${u.user_id}`); onClose(); }}
                  className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-accent transition-colors text-left"
                >
                  <div className="relative">
                    <Avatar user={u} size={40} />
                    <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-card flex items-center justify-center" style={{ background: M.color }}>
                      <Icon className="w-3 h-3 text-white" style={type === "love" ? { fill: "white" } : {}} />
                    </span>
                  </div>
                  <span className="text-sm font-medium flex items-center gap-1">{u.name}{u.verified && <VerifiedBadge size={13} />}</span>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
